import {
    ActionMenu,
    FOCUS_RING,
    formatDate,
    Icon,
    Pills,
    type OptionProps,
} from '@amelio/ui-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import DeletePillarDialog from './DeletePillarDialog';
import PillarLeaveActiveDialog from './PillarLeaveActiveDialog';
import { PILLAR_STATUS_LABEL_KEY, PILLAR_STATUS_PILL } from './pillarStatusDisplay';

import type { Pillar } from '@/types/pillars';

import { useTogglePillarStatus } from '@/api/pillars';
import PillarIcon from '@/components/objectives/PillarIcon';
import { PillarStatus } from '@/constants/PillarStatus';
import { useError } from '@/hooks/utils/useError';
import i18n from '@/plugins/i18n';
import { showSuccessToast } from '@/plugins/Toast';
import { resolveCompanyValueIconBackground } from '@/utils/companyValueIconBackground';
import { getLocalizedText } from '@/utils/localization';
import { isPillarActiveLimitError, showPillarActiveLimitToast } from '@/utils/pillarErrors';

interface PillarCardProps {
    pillar: Pillar;
    onEdit?: () => void;
    onDelete?: () => void;
}

export default function PillarCard({ pillar, onEdit, onDelete }: PillarCardProps) {
    const { t } = useTranslation();
    const { handleError } = useError();
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isDeactivateDialogOpen, setIsDeactivateDialogOpen] = useState(false);
    const toggleStatusMutation = useTogglePillarStatus();

    const isActive = pillar.status === PillarStatus.ACTIVE;
    const displayName = getLocalizedText(pillar.name);
    const displayDescription = getLocalizedText(pillar.description);
    const iconBackground = resolveCompanyValueIconBackground(pillar.icon, pillar.iconBackground);

    const runToggleStatus = async () => {
        try {
            await toggleStatusMutation.mutateAsync(pillar.id);
            showSuccessToast({
                message: t('pillars.toggleStatusSuccess', {
                    name: displayName,
                    action: isActive ? t('pillars.deactivated') : t('pillars.activated'),
                }),
            });
        } catch (error) {
            if (isPillarActiveLimitError(error)) {
                showPillarActiveLimitToast();
                return;
            }
            handleError({ error });
        }
    };

    // Deactivating orphans this pillar's linked objectives, so it needs a confirmation first;
    // activating never does.
    const handleToggleStatus = () => {
        if (isActive) {
            setIsDeactivateDialogOpen(true);
            return;
        }
        runToggleStatus();
    };

    const handleConfirmDeactivate = async () => {
        await runToggleStatus();
        setIsDeactivateDialogOpen(false);
    };

    const menuItems: OptionProps[] = [
        {
            id: 'edit',
            label: t('pillars.edit'),
            onClick: () => onEdit?.(),
            icon: (
                <Icon
                    name={'edit-edit-2'}
                    size={'sm'}
                    color={'inherit'}
                />
            ),
        },
        {
            id: 'toggle-status',
            label: isActive ? t('pillars.deactivate') : t('pillars.activate'),
            onClick: handleToggleStatus,
            icon: (
                <Icon
                    name={isActive ? 'essential-slash' : 'essential-checkmark'}
                    size={'sm'}
                    color={'inherit'}
                />
            ),
        },
        {
            id: 'delete',
            label: t('pillars.delete'),
            onClick: () => setIsDeleteDialogOpen(true),
            danger: true,
            icon: (
                <Icon
                    name={'essential-trash'}
                    size={'sm'}
                    color={'inherit'}
                />
            ),
        },
    ];

    const periodText = pillar.periodStartDate
        ? pillar.periodEndDate
            ? t('pillars.periodRange', {
                  start: formatDate({ date: pillar.periodStartDate, language: i18n.language }),
                  end: formatDate({ date: pillar.periodEndDate, language: i18n.language }),
              })
            : t('pillars.periodSince', {
                  start: formatDate({ date: pillar.periodStartDate, language: i18n.language }),
              })
        : null;

    const openEditor = () => onEdit?.();

    // A click anywhere on the card opens the editor; the keyboard and assistive technology reach it
    // through the button on the name, so the card never wraps the kebab menu inside a control of
    // its own. The two dialogs below are portaled modals, siblings of this tile.
    return (
        <>
            <div
                onClick={openEditor}
                className={
                    'relative flex w-full cursor-pointer gap-4 rounded-xl bg-white/60 p-5 backdrop-blur-2xl transition-shadow hover:shadow-lg sm:p-6'
                }
                data-testid={`pillar-card-${pillar.id}`}
            >
                <div
                    className={
                        'flex h-fit flex-shrink-0 items-center justify-center overflow-hidden rounded-xl p-3 shadow-lg'
                    }
                    style={{ background: iconBackground }}
                >
                    <PillarIcon
                        icon={pillar.icon}
                        size={32}
                    />
                </div>

                <div className={'flex flex-1 flex-col gap-1.5'}>
                    <h3 className={'text-navy text-left text-[20px] font-medium'}>
                        <button
                            type={'button'}
                            onClick={(event) => {
                                event.stopPropagation();
                                openEditor();
                            }}
                            className={`${FOCUS_RING} cursor-pointer text-left`}
                            data-testid={`pillar-card-${pillar.id}-name`}
                        >
                            {displayName}
                        </button>
                    </h3>

                    <p className={'text-[13px] font-normal text-gray-500'}>{displayDescription}</p>

                    {periodText && (
                        <p
                            className={'text-sm text-gray-400'}
                            data-testid={`pillar-card-${pillar.id}-period`}
                        >
                            {periodText}
                        </p>
                    )}
                </div>

                <div className={'flex flex-shrink-0 items-start gap-3'}>
                    <Pills
                        variant={PILLAR_STATUS_PILL[pillar.status].variant}
                        status={PILLAR_STATUS_PILL[pillar.status].status}
                        size={'sm'}
                        className={'py-1.5'}
                    >
                        {t(PILLAR_STATUS_LABEL_KEY[pillar.status])}
                    </Pills>

                    <div onClick={(event) => event.stopPropagation()}>
                        <ActionMenu
                            items={menuItems}
                            ariaLabel={t('pillars.moreOptions')}
                            variant={'subtle'}
                            size={'sm'}
                            className={'!rounded-md border-gray-300 bg-white'}
                        />
                    </div>
                </div>
            </div>

            <DeletePillarDialog
                isOpen={isDeleteDialogOpen}
                pillar={pillar}
                onCancel={() => setIsDeleteDialogOpen(false)}
                onSuccess={() => {
                    setIsDeleteDialogOpen(false);
                    onDelete?.();
                }}
            />

            <PillarLeaveActiveDialog
                isOpen={isDeactivateDialogOpen}
                pillar={pillar}
                targetStatus={PillarStatus.INACTIVE}
                isConfirming={toggleStatusMutation.isPending}
                onCancel={() => setIsDeactivateDialogOpen(false)}
                onConfirm={handleConfirmDeactivate}
            />
        </>
    );
}
